import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/lib/supabase";
import { validatePassword } from "@/utils/passwordValidation";
import { PasswordRequirements } from "@/components/auth/PasswordRequirements";
import { useAuth } from "@/hooks/useAuth";

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loadingForm, setLoadingForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasValidToken, setHasValidToken] = useState(false);
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [isSignupReset, setIsSignupReset] = useState(false);
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  // First effect: Initial direct token check on mount
  useEffect(() => {
    const validateResetToken = async () => {
      console.log("Directly checking token validity...");
      
      // Check if this is a signup reset flow
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('type') === 'signup') {
        setIsSignupReset(true);
      }
      
      // Check if we have a token in the URL hash
      if (window.location.hash) {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const tokenType = hashParams.get('type');
        
        if (accessToken && tokenType === 'recovery') {
          try {
            // Set the session directly using the token
            const { data, error: sessionError } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: hashParams.get('refresh_token') || ''
            });
            
            if (sessionError) {
              console.error("Token validation error:", sessionError);
              setError("Invalid or expired reset link. Please request a new password reset.");
            } else if (data?.user) {
              console.log("Valid token detected, user authenticated:", data.user.email);
              setHasValidToken(true);
            }
          } catch (err) {
            console.error("Error validating token:", err);
            setError("Error processing your request. Please try again.");
          }
        }
      }
      
      setInitialCheckDone(true);
    };

    validateResetToken();
  }, []);

  // Second effect: Check auth state from AuthProvider
  useEffect(() => {
    console.log('ResetPassword useEffect triggered:', { 
      authLoading, 
      user: !!user, 
      initialCheckDone, 
      hasValidToken
    });
    
    // Only proceed if initial check is complete and auth is not loading
    if (initialCheckDone && !authLoading) {
      if (user) {
        console.log("User is authenticated via AuthProvider");
        setHasValidToken(true);
      } else if (!hasValidToken) {
        // Only show error if we haven't already validated a token
        console.log("No authenticated user found after auth check.");
        setError("Invalid or expired reset link. Please request a new password reset.");
      }
    }
  }, [user, authLoading, initialCheckDone, hasValidToken]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    const passwordValidation = validatePassword(newPassword);
    if (!passwordValidation.valid) {
      setError(passwordValidation.message);
      return;
    }
    
    setLoadingForm(true);
    
    try {
      console.log("Updating password...");
      
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      
      console.log("Password updated successfully");
      
      if (isSignupReset) {
        toast.success("Account setup complete!", {
          description: "Your password has been set. You can now sign in to your Hivetivity account."
        });
      } else {
        toast.success("Password updated", {
          description: "Your password has been successfully reset. You can now sign in with your new password."
        });
      }
      
      setTimeout(() => {
        navigate("/auth");
      }, 2000);
    } catch (error: any) {
      console.error("Password update error:", error);
      
      setError(error.message || "An error occurred. Please try again.");
      
      toast.error("Password reset failed", {
        description: error.message || "An error occurred. Please try again."
      });
    } finally {
      setLoadingForm(false);
    }
  };

  // Show loading indicator while checks are in progress
  if (authLoading || !initialCheckDone) {
    console.log('ResetPassword rendering loading spinner because checks are in progress');
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  console.log('ResetPassword rendering main content:', { error: !!error, hasValidToken });

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="w-full max-w-md p-8">
        <Card>
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">
              {isSignupReset ? "Complete Your Account Setup" : "Reset Your Password"}
            </CardTitle>
            <CardDescription className="text-center">
              {isSignupReset 
                ? "Welcome! Set your password to complete your Hivetivity account setup" 
                : "Enter a new password for your account"
              }
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {error ? (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                <p>{error}</p>
                <div className="mt-4">
                  <Button variant="outline" onClick={() => navigate("/auth")}>
                    Return to Sign In
                  </Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handlePasswordReset} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <Input
                    id="password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                  />
                  <PasswordRequirements password={newPassword} />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>
                
                <Button type="submit" className="w-full" disabled={loadingForm}>
                  {loadingForm ? "Updating..." : "Update Password"}
                </Button>
              </form>
            )}
          </CardContent>
          
          <CardFooter className="flex justify-center">
            <Button variant="link" onClick={() => navigate("/auth")}>
              Return to Sign In
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default ResetPassword;
